/**
 * Alluvion Home — Live Frame Preview
 * ====================================
 * Replaces Dawn's product image gallery with a live preview that shows
 * the customer's photo composited into the selected frame mockup.
 *
 * Only runs on: /products/print-your-own-photo
 *
 * INSTALLATION
 * ─────────────────────────────────────────
 * STEP 1 — Add this file as a theme asset
 *   Shopify Admin → Online Store → Themes → Edit code
 *   Assets → Add a new asset → name it alluvion-preview.js → paste this file
 *
 * STEP 2 — Load the script in layout/theme.liquid
 *   Find where you added the alluvion-configurator.js script tag and add this ABOVE it:
 *
 *   {% if request.path contains '/products/print-your-own-photo' %}
 *     <script src="{{ 'alluvion-preview.js' | asset_url }}" defer></script>
 *   {% endif %}
 *
 * STEP 3 — Set alt text on your product images in Shopify Admin
 *   Each mockup image uploaded to the product must have its alt text set exactly as:
 *
 *   black-frame-no-passepartout-portrait
 *   black-frame-passepartout-portrait
 *   white-frame-no-passepartout-portrait
 *   white-frame-passepartout-portrait
 *   dark-oak-frame-no-passepartout-portrait
 *   dark-oak-frame-passepartout-portrait
 *   light-oak-frame-no-passepartout-portrait
 *   light-oak-frame-passepartout-portrait
 *   print-only-no-passepartout-portrait
 *   print-only-passepartout-portrait
 *   black-frame-no-passepartout-landscape
 *   black-frame-passepartout-landscape
 *   white-frame-no-passepartout-landscape
 *   white-frame-passepartout-landscape
 *   dark-oak-frame-no-passepartout-landscape
 *   dark-oak-frame-passepartout-landscape
 *   light-oak-frame-no-passepartout-landscape
 *   light-oak-frame-passepartout-landscape
 *   print-only-no-passepartout-landscape
 *   print-only-passepartout-landscape
 *
 * ─────────────────────────────────────────
 * HOW IT WORKS
 * ─────────────────────────────────────────
 * - Default view shows print-only-no-passepartout-portrait
 * - Selecting a style switches to the matching mockup
 * - Uploading a photo composites it into the frame window
 * - Landscape photos automatically switch to landscape mockups
 * - Passepartout option scales the photo to 70.7% (1/√2) within the window
 */

(function () {
  'use strict';

  /* ── Guard ────────────────────────────────────────────────────────── */
  if (!window.location.pathname.includes('/products/print-your-own-photo')) return;

  /* ── Artwork window coordinates (as % of mockup image size) ─────── */
  /* These define where the customer photo gets placed in each mockup.  */
  /* For passepartout, the photo is scaled to 70.7% within the window. */
  var WINDOWS = {
    /* PORTRAIT - No Passepartout */
    'black-frame-no-passepartout-portrait':     { left: 30.0, top: 13.9, width: 39.9, height: 69.9 },
    'white-frame-no-passepartout-portrait':     { left: 30.0, top: 14.0, width: 39.9, height: 69.7 },
    'dark-oak-frame-no-passepartout-portrait':  { left: 30.0, top: 14.0, width: 39.9, height: 70.1 },
    'light-oak-frame-no-passepartout-portrait': { left: 30.0, top: 14.0, width: 39.9, height: 69.7 },
    'print-only-no-passepartout-portrait':      { left: 30.0, top: 13.9, width: 40.0, height: 70.0 },
    /* PORTRAIT - Passepartout (photo scaled to 70.7% within same window) */
    'black-frame-passepartout-portrait':        { left: 37.1, top: 23.8, width: 25.7, height: 50.0 },
    'white-frame-passepartout-portrait':        { left: 37.1, top: 23.8, width: 25.7, height: 50.0 },
    'dark-oak-frame-passepartout-portrait':     { left: 37.1, top: 23.8, width: 25.7, height: 50.0 },
    'light-oak-frame-passepartout-portrait':    { left: 37.1, top: 23.8, width: 25.7, height: 50.0 },
    'print-only-passepartout-portrait':         { left: 37.1, top: 23.8, width: 25.7, height: 50.0 },
    /* LANDSCAPE - No Passepartout */
    'black-frame-no-passepartout-landscape':    { left: 23.1, top: 26.2, width: 56.0, height: 49.9 },
    'white-frame-no-passepartout-landscape':    { left: 23.1, top: 26.2, width: 56.0, height: 49.9 },
    'dark-oak-frame-no-passepartout-landscape': { left: 23.2, top: 25.9, width: 56.0, height: 49.9 },
    'light-oak-frame-no-passepartout-landscape':{ left: 23.2, top: 26.2, width: 55.7, height: 49.9 },
    'print-only-no-passepartout-landscape':     { left: 22.2, top: 26.5, width: 55.9, height: 50.0 },
    /* LANDSCAPE - Passepartout */
    'black-frame-passepartout-landscape':       { left: 31.0, top: 35.0, width: 40.1, height: 32.0 },
    'white-frame-passepartout-landscape':       { left: 31.0, top: 35.0, width: 40.1, height: 32.0 },
    'dark-oak-frame-passepartout-landscape':    { left: 31.0, top: 35.0, width: 40.1, height: 32.0 },
    'light-oak-frame-passepartout-landscape':   { left: 31.0, top: 35.0, width: 40.1, height: 32.0 },
    'print-only-passepartout-landscape':        { left: 30.3, top: 35.6, width: 39.9, height: 31.9 },
  };

  /* Style name → mockup key prefix */
  var STYLE_KEY = {
    'Print Only':      'print-only',
    'Black Frame':     'black-frame',
    'White Frame':     'white-frame',
    'Dark Oak Frame':  'dark-oak-frame',
    'Light Oak Frame': 'light-oak-frame',
  };

  /* ── State ────────────────────────────────────────────────────────── */
  var currentStyle      = null;
  var currentPasse      = false;
  var currentOrientation = 'portrait'; /* 'portrait' | 'landscape' */
  var photoDataUrl      = null;
  var productImages     = {}; /* alt text → src URL */

  /* ── Boot ─────────────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    collectProductImages();
    injectPreviewStyles();
    replaceGallery();
    listenForConfiguratorEvents();
  });

  /* ── Collect product image URLs by alt text ──────────────────────── */
  function collectProductImages() {
    /* Dawn renders product images in <img> tags with data-src or src */
    var imgs = document.querySelectorAll(
      '.product__media img, .product-single__media img, [data-media-type="image"] img'
    );
    imgs.forEach(function (img) {
      var alt = (img.alt || '').trim().toLowerCase();
      var src = img.src || img.dataset.src || '';
      if (alt && src && WINDOWS[alt]) {
        /* Get the highest quality version by removing size constraints */
        src = src.replace(/(_\d+x\d+|_\d+x)(\.\w+)(\?|$)/, '$2$3');
        productImages[alt] = src;
      }
    });

    /* Also try srcset for higher res */
    imgs.forEach(function (img) {
      var alt = (img.alt || '').trim().toLowerCase();
      if (!alt || !WINDOWS[alt]) return;
      var srcset = img.srcset || img.dataset.srcset || '';
      if (srcset) {
        /* Pick the largest from srcset */
        var parts = srcset.split(',').map(function (s) { return s.trim().split(/\s+/); });
        var largest = parts.reduce(function (best, curr) {
          var w = parseInt((curr[1] || '0').replace('w', ''), 10);
          return w > best.w ? { url: curr[0], w: w } : best;
        }, { url: '', w: 0 });
        if (largest.url) productImages[alt] = largest.url;
      }
    });

    console.log('[Alluvion Preview] Found ' + Object.keys(productImages).length + ' mockup images');
  }

  /* ── Build mockup key from current state ─────────────────────────── */
  function getMockupKey() {
    var styleKey = currentStyle ? (STYLE_KEY[currentStyle] || 'print-only') : 'print-only';
    var passeKey = currentPasse ? 'passepartout' : 'no-passepartout';
    return styleKey + '-' + passeKey + '-' + currentOrientation;
  }

  /* ── Replace Dawn's gallery with our preview container ──────────── */
  function replaceGallery() {
    var gallery = document.querySelector(
      '.product__media-wrapper, .product__media-list, .product-single__media-wrapper, [data-product-media-list]'
    );
    if (!gallery) {
      console.warn('[Alluvion Preview] Could not find product gallery element');
      return;
    }

    /* Hide only Dawn's media list, not the whole wrapper */
var mediaList = gallery.querySelector('media-gallery, .product__media-list, slider-component');
if (mediaList) {
  mediaList.style.display = 'none';
}

    /* Build our preview container */
    var container = document.createElement('div');
    container.id = 'alv-preview-container';
    container.innerHTML =
      '<div id="alv-preview-frame">' +
        '<img id="alv-preview-mockup" src="" alt="Frame preview">' +
        '<div id="alv-preview-photo-wrap">' +
          '<img id="alv-preview-photo" src="" alt="Your photo preview">' +
        '</div>' +
        '<div id="alv-preview-placeholder">' +
          '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" opacity="0.3"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>' +
          '<p>Upload your photo to see a preview</p>' +
        '</div>' +
      '</div>';

    gallery.appendChild(container);
    renderPreview();
  }

  /* ── Render the preview ──────────────────────────────────────────── */
  function renderPreview() {
    var mockupImg    = document.getElementById('alv-preview-mockup');
    var photoWrap    = document.getElementById('alv-preview-photo-wrap');
    var photoImg     = document.getElementById('alv-preview-photo');
    var placeholder  = document.getElementById('alv-preview-placeholder');
    if (!mockupImg) return;

    var key = getMockupKey();
    var win = WINDOWS[key];
    var src = productImages[key];

    /* Update mockup image */
    if (src) {
      mockupImg.src = src;
      mockupImg.style.display = 'block';
    } else {
      /* Fallback to default */
      var defaultKey = 'print-only-no-passepartout-' + currentOrientation;
      var defaultSrc = productImages[defaultKey];
      if (defaultSrc) {
        mockupImg.src = defaultSrc;
        win = WINDOWS[defaultKey];
      }
      mockupImg.style.display = 'block';
    }

    /* Position the photo overlay */
    if (win && photoDataUrl) {
      var scale = win.passe ? 0.707 : 1;

      /* Photo wrap sits exactly over the artwork window */
      photoWrap.style.display    = 'block';
      photoWrap.style.left       = win.left + '%';
      photoWrap.style.top        = win.top + '%';
      photoWrap.style.width      = win.width + '%';
      photoWrap.style.height     = win.height + '%';

      /* Photo itself is scaled to 70.7% for passepartout, centred */
      photoImg.src               = photoDataUrl;
      photoImg.style.width       = (scale * 100) + '%';
      photoImg.style.height      = (scale * 100) + '%';
      photoImg.style.left        = ((1 - scale) / 2 * 100) + '%';
      photoImg.style.top         = ((1 - scale) / 2 * 100) + '%';

      placeholder.style.display  = 'none';
    } else if (win && !photoDataUrl) {
      photoWrap.style.display    = 'none';
      placeholder.style.display  = 'flex';
    } else {
      photoWrap.style.display    = 'none';
      placeholder.style.display  = 'none';
    }
  }

  /* ── Listen for events from alluvion-configurator.js ────────────── */
  /* The configurator dispatches custom events when state changes       */
  function listenForConfiguratorEvents() {
    document.addEventListener('alv:style-changed', function (e) {
      currentStyle = e.detail.style;
      renderPreview();
    });

    document.addEventListener('alv:passe-changed', function (e) {
      currentPasse = e.detail.passe;
      renderPreview();
    });

    document.addEventListener('alv:photo-changed', function (e) {
      photoDataUrl       = e.detail.dataUrl;
      currentOrientation = e.detail.orientation || 'portrait';
      renderPreview();
    });

    document.addEventListener('alv:photo-cleared', function () {
      photoDataUrl       = null;
      currentOrientation = 'portrait';
      renderPreview();
    });
  }

  /* ── Inject preview CSS ──────────────────────────────────────────── */
  function injectPreviewStyles() {
    if (document.getElementById('alv-preview-styles')) return;
    var style = document.createElement('style');
    style.id = 'alv-preview-styles';
    style.textContent = [
      '#alv-preview-container {',
      '  width: 100%;',
      '}',

      '#alv-preview-frame {',
      '  position: relative;',
      '  width: 100%;',
      '  line-height: 0;',
      '  border-radius: var(--media-radius, 4px);',
      '  overflow: hidden;',
      '  background: rgb(var(--color-background));',
      '}',

      '#alv-preview-mockup {',
      '  width: 100%;',
      '  height: auto;',
      '  display: block;',
      '}',

      '#alv-preview-photo-wrap {',
      '  position: absolute;',
      '  overflow: hidden;',
      '  display: none;',
      '}',

      '#alv-preview-photo {',
      '  position: absolute;',
      '  width: 100%;',
      '  height: 100%;',
      '  object-fit: cover;',
      '}',

      '#alv-preview-placeholder {',
      '  position: absolute;',
      '  inset: 0;',
      '  display: flex;',
      '  flex-direction: column;',
      '  align-items: center;',
      '  justify-content: center;',
      '  gap: .8rem;',
      '  pointer-events: none;',
      '}',

      '#alv-preview-placeholder p {',
      '  font-size: 1.2rem;',
      '  color: rgba(var(--color-foreground), .4);',
      '  margin: 0;',
      '  font-family: var(--font-body-family);',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }

})();